/**
 * CR403: Merchant Categories Admin Page
 */

import { useState } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useAuth } from "@/contexts/AuthContext";
import {
  useMerchantCategoriesList,
  useMerchantCategoryMutations,
  type MerchantCategoryRow,
} from "@/hooks/useMerchantCategories";
import {
  CategoryList,
  CategoryDetail,
  AddCategoryModal,
} from "@/components/admin/merchant-categories";
import { FolderOpen } from "lucide-react";

export default function MerchantCategories() {
  const { isAdmin } = useAuth();
  const { data: categories = [], isLoading } = useMerchantCategoriesList();
  const {
    createCategory,
    isCreating,
    updateCategory,
    isUpdating,
    deleteCategory,
    isDeleting,
  } = useMerchantCategoryMutations();

  const [selectedCategory, setSelectedCategory] = useState<MerchantCategoryRow | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  // Auto-select first category when data loads
  if (!selectedCategory && categories.length > 0 && !isLoading) {
    setSelectedCategory(categories[0]);
  }

  // Update selected category when it changes in the list
  const currentSelected = selectedCategory
    ? categories.find((c) => c.id === selectedCategory.id) || null
    : null;

  if (!isAdmin) {
    return (
      <AdminLayout>
        <div className="text-center py-12 text-muted-foreground">
          Admin access required
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="h-[calc(100vh-4rem)]">
        {/* Header */}
        <div className="px-6 py-4 border-b">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FolderOpen className="h-6 w-6" />
            Categories
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage merchant categories. Merchants inherit their category, but sources can override.
          </p>
        </div>

        {/* Split View */}
        <div className="grid grid-cols-[320px_1fr] h-[calc(100%-5rem)]">
          {/* Left: Category List */}
          <CategoryList
            categories={categories}
            selectedId={currentSelected?.id || null}
            onSelect={setSelectedCategory}
            onAddNew={() => setShowAddModal(true)}
            isLoading={isLoading}
          />

          {/* Right: Detail Panel */}
          <div className="overflow-auto">
            {currentSelected ? (
              <CategoryDetail
                category={currentSelected}
                allCategories={categories}
                onUpdate={updateCategory}
                onDelete={deleteCategory}
                isUpdating={isUpdating}
                isDeleting={isDeleting}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                {isLoading ? "Loading..." : "Select a category to view details"}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Modal */}
      <AddCategoryModal
        open={showAddModal}
        onOpenChange={setShowAddModal}
        onCreate={createCategory}
        isCreating={isCreating}
      />
    </AdminLayout>
  );
}
